/**
 * Contrato e chamadas do pacote de Kick Off (spec §2.5, tela 11).
 *
 * **O que a F9 substitui.** Hoje o consultor cola o despejo de um Google Forms na descrição da
 * issue `Kick Off` do ONB — a ONB-3114 começa literalmente com "Carimbo de data/hora". O pacote
 * consolida as respostas de todas as trilhas numa descrição estruturada e cria a issue no épico
 * da implantação. O gate declarado da fatia é esse: *issue Kick Off aceita pelo consultor sem
 * edição manual*.
 *
 * Quatro regras moram aqui:
 *
 * · **Gerar já tenta enviar.** Não existe rota separada de envio ao Jira, e a ausência é decisão:
 *   um segundo botão deixaria o consultor com o retrato pronto e nenhuma issue — que é exatamente
 *   o estado de hoje, com o formulário colado à mão.
 *
 * · **Jira desligado é estado normal, não erro.** `PORTAL_JIRA_TOKEN` e `PORTAL_JIRA_EMAIL` estão
 *   vazios no servidor de propósito: nenhuma issue de teste foi criada no ONB real. O pacote
 *   nasce `201` com `jira_kickoff_key` nula e `jira_pendencia` escrita. A tela chama isso de
 *   **pendência**, e não de falha, pelo mesmo motivo do nome do campo: uma tela que chame de erro
 *   o estado normal do ambiente ensina quem opera a ignorar alerta.
 *
 * · **`desatualizado` não é "existe versão mais nova"** — isso é a ordem de `versao`. É o gatilho
 *   do banco dizendo que alguma resposta da implantação mudou DEPOIS deste retrato. É o aviso que
 *   impede o consultor de colar no Jira uma foto velha sem perceber, e por isso ele é o dado mais
 *   gritante da tela.
 *
 * · **Lacuna é conteúdo do pacote, não ausência dele.** Pergunta não respondida e trilha não
 *   enviada saem escritas, no texto da issue e nesta listagem. Silêncio no lugar do buraco é o
 *   defeito do Google Forms que a F9 veio encerrar.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { enviarJson, pedirJson } from "./api";

/* --------------------------------------------------------------------------- vocabulário */

/** Os formatos que a descrição aceita. `adf` é o que a `/rest/api/3` do Jira exige. */
export const FORMATOS_DA_DESCRICAO = ["adf", "wiki_markup"] as const;

const esquemaFormato = z.enum(FORMATOS_DA_DESCRICAO);
const esquemaTipoDeArtefato = z.enum(["descricao_jira", "docx", "xlsx"]);

export type FormatoDaDescricao = z.infer<typeof esquemaFormato>;
export type TipoDeArtefato = z.infer<typeof esquemaTipoDeArtefato>;

/* ------------------------------------------------------------------------------ esquemas */

const esquemaPessoa = z.object({
  id: z.string().min(1),
  nome: z.string().min(1),
  email: z.string().min(1),
});

const esquemaLacuna = z.object({
  /* Vocabulário fechado no servidor, texto aqui: a tela AGRUPA por ele, e um motivo novo que
     esta camada não conhecesse sumiria do agrupamento em vez de aparecer sem rótulo. */
  motivo: z.string().min(1),
  trilha: z.string().nullable(),
  pergunta_id: z.string().nullable(),
  descricao: z.string().min(1),
});

const esquemaArtefato = z.object({
  tipo: esquemaTipoDeArtefato,
  formato: esquemaFormato.nullable(),
  /* O corpo exato que vai (ou foi) para o Jira — em `adf`, o JSON serializado do documento. */
  conteudo: z.string().nullable(),
  /* A projeção legível do MESMO conteúdo gravado. Vem do servidor em vez de ser reconstruída
     aqui: reconstruir mostraria as respostas de hoje ao lado de um artefato de ontem. */
  texto: z.string().nullable(),
  caracteres: z.number().nullable(),
  tamanho: z.number().nullable(),
  gerado_em: z.string().min(1),
});

const esquemaPacote = z.object({
  id: z.string().min(1),
  implantacao_id: z.string().min(1),
  versao: z.number(),
  gerado_em: z.string().min(1),
  gerado_por: esquemaPessoa.nullable(),
  desatualizado: z.boolean(),
  desatualizado_em: z.string().nullable(),
  jira_kickoff_key: z.string().nullable(),
  jira_enviado_em: z.string().nullable(),
  jira_pendencia: z.string().nullable(),
  lacunas: z.array(esquemaLacuna),
});

const esquemaPacoteDetalhado = esquemaPacote.extend({
  artefatos: z.array(esquemaArtefato),
});

const esquemaListaDePacotes = z.object({
  implantacao_id: z.string().min(1),
  pacotes: z.array(esquemaPacote),
  total: z.number(),
});

export type LacunaDoPacote = z.infer<typeof esquemaLacuna>;
export type ArtefatoDoPacote = z.infer<typeof esquemaArtefato>;
export type PacoteNaListagem = z.infer<typeof esquemaPacote>;
export type PacoteDetalhado = z.infer<typeof esquemaPacoteDetalhado>;
export type ListaDePacotes = z.infer<typeof esquemaListaDePacotes>;

/* ------------------------------------------------------------------------------ consultas */

export const CHAVE_RAIZ_DOS_PACOTES = ["pacotes"] as const;

export function chaveDosPacotes(implantacaoId: string) {
  return ["pacotes", "implantacao", implantacaoId] as const;
}

export function chaveDoPacote(pacoteId: string) {
  return ["pacotes", "versao", pacoteId] as const;
}

/** As versões do pacote, da mais nova para a mais antiga — a última é a que o consultor usa. */
export function usePacotes(implantacaoId: string) {
  return useQuery<ListaDePacotes>({
    queryKey: chaveDosPacotes(implantacaoId),
    queryFn: ({ signal }) =>
      pedirJson(
        `/implantacoes/${encodeURIComponent(implantacaoId)}/pacotes`,
        esquemaListaDePacotes,
        { signal },
      ),
    enabled: implantacaoId.length > 0,
    retry: false,
  });
}

/** Uma versão, com a descrição que vai para a issue. */
export function usePacote(pacoteId: string) {
  return useQuery<PacoteDetalhado>({
    queryKey: chaveDoPacote(pacoteId),
    queryFn: ({ signal }) =>
      pedirJson(`/pacotes/${encodeURIComponent(pacoteId)}`, esquemaPacoteDetalhado, { signal }),
    enabled: pacoteId.length > 0,
    retry: false,
  });
}

/**
 * Gera uma versão do pacote e tenta criar a issue de Kick Off no mesmo ato.
 *
 * Falhar no envio **não desfaz a geração**: o pacote fica gravado com a pendência escrita. Por
 * isso o `onSuccess` invalida a listagem em vez de só empilhar o resultado — a versão anterior
 * pode ter sido marcada como desatualizada no caminho, e duas telas contando versões diferentes
 * é o começo de alguém colar o retrato errado.
 */
export function useGerarPacote(implantacaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (formato: FormatoDaDescricao) =>
      enviarJson(
        `/implantacoes/${encodeURIComponent(implantacaoId)}/pacotes`,
        esquemaPacoteDetalhado,
        { formato },
      ),
    onSuccess: (pacote) => {
      cliente.setQueryData<PacoteDetalhado>(chaveDoPacote(pacote.id), pacote);
      void cliente.invalidateQueries({ queryKey: chaveDosPacotes(implantacaoId) });
      void cliente.invalidateQueries({ queryKey: ["console"] });
      void cliente.invalidateQueries({ queryKey: ["implantacoes"] });
    },
  });
}

/* --------------------------------------------------------------------- rótulos e leitura */

/** O que cada motivo de lacuna significa em uma linha, para a tela agrupar por gravidade. */
export const ROTULO_DA_LACUNA: Record<string, string> = {
  trilha_nao_enviada: "Trilha não enviada",
  /* Só a obrigatória em branco vira lacuna (spec de 28/09, 6.10) — a opcional continua escrita
     no texto do resumo, mas não é pendência. */
  pergunta_sem_resposta: "Pergunta obrigatória sem resposta",
  modulo_sem_trilha: "Módulo contratado sem perguntas no kickoff",
  /* Aviso, não impedimento (6.9): pode ser legítima, e nada trava por ela. */
  contradicao: "Aviso: divergência entre trilhas",
};

export function rotuloDaLacuna(motivo: string): string {
  return ROTULO_DA_LACUNA[motivo] ?? motivo;
}

export const ROTULO_DO_FORMATO: Record<FormatoDaDescricao, string> = {
  adf: "Para envio automático",
  wiki_markup: "Para colar no Jira",
};

/** Com a K-O no GeoTask, o resumo é consulta: o formato não é mais "para o Jira". */
export const ROTULO_DO_FORMATO_NO_GEOTASK: Record<FormatoDaDescricao, string> = {
  adf: "Formato estruturado",
  wiki_markup: "Texto legível",
};

/** A descrição consolidada de um pacote, que é o artefato que vira a issue. */
export function descricaoDo(pacote: PacoteDetalhado): ArtefatoDoPacote | null {
  return pacote.artefatos.find((artefato) => artefato.tipo === "descricao_jira") ?? null;
}

/**
 * Em que pé está a issue de Kick Off deste retrato.
 *
 * **Não existe "enviar ao Jira" como ato separado**, e é isso que este estado tem de contar sem
 * mentir: a tentativa faz parte da geração. Quando ela não deu certo, o caminho é gerar uma
 * versão nova — que é o certo de qualquer jeito, porque entre uma coisa e outra as respostas
 * podem ter mudado, e reenviar o retrato velho seria pior do que não enviar.
 */
export type EstadoDoJira =
  | { estado: "enviado"; chave: string; quando: string | null }
  | { estado: "pendente"; motivo: string }
  | { estado: "sem_registro" };

export function estadoDoJira(pacote: PacoteNaListagem): EstadoDoJira {
  if (pacote.jira_kickoff_key) {
    return {
      estado: "enviado",
      chave: pacote.jira_kickoff_key,
      quando: pacote.jira_enviado_em,
    };
  }
  if (pacote.jira_pendencia) return { estado: "pendente", motivo: pacote.jira_pendencia };
  /* Nem chave nem pendência: o servidor não registrou tentativa nenhuma. Dizer "pendente" aqui
     inventaria um diagnóstico que ninguém escreveu. */
  return { estado: "sem_registro" };
}
